import { forwardRef, FocusEvent } from "react";
import { getBasePropsFromHtmlProps } from "../../utilities/getBasePropsFromHtmlProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { Body } from "capitalrx-components-2.0/lib/typography";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { LoadingGroup } from "capitalrx-components-2.0/lib/general";
import { RadioProps } from "./Radio";
import { Label } from "../shared";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { useSubmitting } from "../SubmitGroup";
import { Dot } from "./Dot";
import { useValidity } from "../ValidityGroup";
import { createComponentStyleHook } from "capitalrx-components-2.0/lib/tokens";
import { getStyleModeSafeProps } from "../utils/getStyleModeSafeProps";
import tokenStyles from "./styles.module.css";
import legacyStyles from "./styles.module.scss";
import { SharedRadioPlaceholder } from "./SharedRadioPlaceholder";

const useResolvedSharedRadioStyles = createComponentStyleHook<SharedRadioProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "radio",
});

/**
 * The `SharedRadio` component properties.
 */
export interface SharedRadioProps
  extends Omit<
    RadioProps,
    "instructions" | "hideLabel" | "hideInstructions" | "onFocusEnter" | "onFocusExit" | "className" | "style"
  > {
  /**
   * The input ID.
   */
  readonly id: string;

  /**
   * Function to call when the radio is focused.
   */
  readonly onFocus?: (ev: FocusEvent) => void;

  /**
   * Function to call when the radio is unfocused.
   */
  readonly onBlur?: (ev: FocusEvent) => void;
}

/**
 * The shared radio component used by the `Radio`, `Radio.Field` and `RadioGroup` components.
 */
export const SharedRadio = forwardRef<HTMLInputElement, SharedRadioProps>(function SharedRadio(props, ref) {
  const { id, label, tabIndex, autoFocus, defaultValue = null, onChange, onFocus, onBlur, tooltip, unpadded } = props;
  const { value = defaultValue } = props;

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);
  const [invalid] = useValidity();
  const submitting = useSubmitting();

  const selected = value === true;
  const { mode, styles, StyleModeBoundary } = useResolvedSharedRadioStyles(getStyleModeSafeProps(props));

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [
      styles.SharedRadio,
      "shared-radio",
      {
        selected,
        invalid,
        disabled,
        readonly: readOnly || submitting,
        unpadded,
      },
    ],
    testIdPrefix: "shared-radio",
  });

  return (
    <StyleModeBoundary mode={mode}>
      <LoadingGroup.Placeholder fallback={<SharedRadioPlaceholder />}>
        <Body {...getBasePropsFromHtmlProps(baseHtmlProps)}>
          <Shelf spacing="none" alignment="top">
            <Dot
              id={id}
              ref={ref}
              tabIndex={tabIndex}
              autoFocus={autoFocus}
              value={value}
              defaultValue={defaultValue}
              disabled={disabled}
              readOnly={readOnly}
              onChange={onChange}
              onFocus={onFocus}
              onBlur={onBlur}
            />
            {/* disabled this rule in this file because Label
            is managing the association with the radio through forId prop
            but eslint can't detect that
          */}
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <Label forId={id} tooltip={tooltip} size="small" appearance="regular">
              {label}
            </Label>
            <Shelf.Spacer />
          </Shelf>
        </Body>
      </LoadingGroup.Placeholder>
    </StyleModeBoundary>
  );
});
